import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Wifi, Shield, Eye, Radio, Lock, Send, AlertTriangle } from "lucide-react";
import { MP } from "./MysticalBg";
import { DT } from "./DeltaTypography";

const logoImg = "/assets/new-logo.png";

function LiveClock() {
  const [time, setTime] = useState(new Date().toLocaleTimeString("es-MX", { hour12: false }));
  useEffect(() => { const iv = setInterval(() => setTime(new Date().toLocaleTimeString("es-MX", { hour12: false })), 1000); return () => clearInterval(iv); }, []);
  return <span style={{ fontFamily: DT.terminal, fontSize: "1rem", color: "rgba(255,160,40,0.95)", letterSpacing: "0.15em", textShadow: "0 0 10px rgba(255,160,40,0.5), 0 0 22px rgba(200,120,20,0.35)" }}>{time}</span>;
}

function ThreatTicker() {
  const THREATS = [
    "Intento de phishing bloqueado - MX-CDMX", "Extorsionador identificado - Operativo activo",
    "Perfil falso eliminado - Red social", "Montadeudas neutralizado - 3 contactos protegidos",
    "Rastreo completado - Familiar localizado", "Datos comprometidos detectados - Alerta enviada",
    "Acceso no autorizado bloqueado - Cifrado activo",
  ];
  return (
    <div className="overflow-hidden" style={{ maskImage: "linear-gradient(90deg, transparent, black 5%, black 95%, transparent)" }}>
      <motion.div className="flex gap-10 whitespace-nowrap" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }}>
        {[...THREATS, ...THREATS].map((t, i) => (
          <span key={i} style={{ fontFamily: DT.data, fontSize: "0.5rem", color: "rgba(255,255,255,0.8)", letterSpacing: "0.12em", flexShrink: 0 }}>
            <motion.span animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}>&#x26A0;</motion.span>
            {" "}{t}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

function PulseIndicator({ color }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 10, height: 10 }}>
      <motion.div className="absolute rounded-full" style={{ width: 10, height: 10, background: color, opacity: 0.3 }}
        animate={{ scale: [1, 2.2, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 2, repeat: Infinity }} />
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer data-testid="site-footer" className="relative z-20" style={{
      borderTop: "1px solid rgba(107,0,21,0.3)", background: "rgba(4,2,8,0.97)", backdropFilter: "blur(20px)",
      boxShadow: "0 -1px 0 rgba(107,0,21,0.2), 0 -4px 30px rgba(0,0,0,0.8)",
    }}>
      {/* Ticker de amenazas */}
      <div className="px-6 py-1.5" style={{ borderBottom: "1px solid rgba(107,0,21,0.12)", background: "rgba(8,0,2,0.6)" }}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <PulseIndicator color="rgba(107,0,21,0.85)" />
            <span style={{ fontFamily: DT.data, fontSize: "0.5rem", color: "rgba(255,160,40,0.85)", letterSpacing: "0.2em" }}>AMENAZAS DETECTADAS</span>
          </div>
          <div style={{ width: 1, height: 14, background: "rgba(107,0,21,0.3)" }} />
          <div className="flex-1 overflow-hidden"><ThreatTicker /></div>
        </div>
      </div>

      {/* Contenido principal del footer */}
      <div className="px-6 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Logo + nombre */}
          <div className="flex items-center gap-3">
            <motion.div animate={{ filter: ["drop-shadow(0 0 4px rgba(107,0,21,0.4))", "drop-shadow(0 0 10px rgba(107,0,21,0.7))", "drop-shadow(0 0 4px rgba(107,0,21,0.4))"] }}
              transition={{ duration: 4, repeat: Infinity }}>
              <img src={logoImg} alt="logo" style={{ width: 36, height: 36, objectFit: "contain", mixBlendMode: "lighten" }} />
            </motion.div>
            <div>
              <p style={{ fontFamily: DT.terminal, fontSize: "0.7rem", color: "rgba(200,120,20,0.85)", letterSpacing: "0.16em", textShadow: "0 0 10px rgba(200,120,20,0.5), 0 0 22px rgba(140,80,10,0.3)", fontWeight: 600 }}>DELTA PROTEC INTEL TRACKING SYSTEM</p>
              <p style={{ fontFamily: DT.data, fontSize: "0.5rem", color: MP.parchmentDim, letterSpacing: "0.1em" }}>Omniscient Observer & Global Justice</p>
            </div>
            <div style={{ width: 1, height: 32, background: "rgba(107,0,21,0.2)", margin: "0 8px" }} />
            <div className="flex flex-col items-center">
              <LiveClock />
              <span style={{ fontFamily: DT.data, fontSize: "0.45rem", color: "rgba(255,255,255,0.75)", letterSpacing: "0.12em" }}>TIEMPO OPERATIVO</span>
            </div>
          </div>

          {/* Indicadores de estado */}
          <div className="flex items-center gap-4">
            {[
              { icon: Wifi, label: "ACTIVO", color: "rgba(155,0,255,0.85)", speed: 2.2 },
              { icon: Shield, label: "CIFRADO", color: "rgba(200,120,20,0.8)", speed: 3 },
              { icon: Eye, label: "OBSERVANDO", color: "rgba(107,0,21,0.85)", speed: 1.8 },
              { icon: Lock, label: "SEGURO", color: "rgba(200,120,20,0.75)", speed: 2.5 },
              { icon: Radio, label: "EN LÍNEA", color: "rgba(155,0,255,0.8)", speed: 2 },
              { icon: AlertTriangle, label: "24/7", color: "rgba(107,0,21,0.8)", speed: 1.5 },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center gap-1">
                <PulseIndicator color={item.color} />
                <motion.div animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: item.speed, repeat: Infinity }}>
                  <item.icon size={11} color={item.color} />
                </motion.div>
                <span style={{ fontFamily: DT.data, fontSize: "0.45rem", color: item.color, letterSpacing: "0.08em", textShadow: `0 0 6px ${item.color}, 0 0 14px ${item.color}66`, fontWeight: 500 }}>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Contacto + Máscara Omniscient */}
          <div className="flex flex-col items-end gap-2">
            <a href="https://t.me/Unit_Intel_Delta_Bot" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              <motion.div whileHover={{ scale: 1.05 }} className="flex items-center gap-1.5">
                <Send size={11} color="rgba(255,160,40,0.85)" style={{ filter: "drop-shadow(0 0 4px rgba(255,160,40,0.6))" }} />
                <span style={{ fontFamily: DT.data, fontSize: "0.5rem", color: "rgba(255,160,40,0.85)", letterSpacing: "0.12em", textShadow: "0 0 8px rgba(255,160,40,0.5), 0 0 18px rgba(200,120,20,0.35)" }}>@Unit_Intel_Delta_Bot</span>
              </motion.div>
            </a>

            {/* MÁSCARA OMNISCIENT */}
            <div className="flex items-center gap-2 mt-1">
              <motion.div
                animate={{ 
                  boxShadow: [
                    "0 0 10px rgba(155,0,255,0.4), 0 0 25px rgba(100,0,180,0.2)",
                    "0 0 20px rgba(155,0,255,0.7), 0 0 40px rgba(100,0,180,0.4)",
                    "0 0 10px rgba(155,0,255,0.4), 0 0 25px rgba(100,0,180,0.2)"
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{
                  width: 32, height: 32,
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(155,0,255,0.3) 0%, rgba(0,0,0,0.8) 70%)",
                  border: "1px solid rgba(155,0,255,0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Eye size={16} color="rgba(155,0,255,0.9)" />
              </motion.div>
              <div>
                <p style={{ fontFamily: DT.greek, fontSize: "0.65rem", color: "rgba(155,0,255,0.9)", letterSpacing: "0.15em", textShadow: "0 0 10px rgba(155,0,255,0.5), 0 0 20px rgba(100,0,180,0.35)", lineHeight: 1 }}>
                  ΔΞLTΛ-VOID
                </p>
                <p style={{ fontFamily: DT.data, fontSize: "0.42rem", color: "rgba(200,200,220,0.7)", letterSpacing: "0.1em" }}>
                  OBSERVER ACTIVE
                </p>
              </div>
            </div>

            <span style={{ fontFamily: DT.data, fontSize: "0.5rem", color: MP.parchmentDim, letterSpacing: "0.1em" }}>© 2026 DELTA PROTEC - GLOBAL JUSTICE</span>
            <motion.span animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 3, repeat: Infinity }}
              style={{ fontFamily: DT.data, fontSize: "0.48rem", color: "rgba(200,120,20,0.6)", letterSpacing: "0.1em" }}>
              HACIENDO EL INTERNET MÁS SEGURO
            </motion.span>
          </div>
        </div>
      </div>

      {/* Línea decorativa inferior */}
      <div style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(155,0,255,0.5), rgba(200,120,20,0.4), rgba(155,0,255,0.5), transparent)" }} />
    </footer>
  );
}
